"use client";
import React, { useState, useEffect } from "react";
import { Code } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";

interface NavLinkProps {
  href: string;
  children: React.ReactNode;
}

const NavLink: React.FC<NavLinkProps> = ({ href, children }) => (
  <a
    href={href}
    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
  >
    {children}
  </a>
);

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { user, logout } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
        setIsExpanded(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const shouldCollapse = isScrolled && !isExpanded;

  const handleLogoClick = () => {
    if (isScrolled) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleLogout = async () => {
    await logout();
    router.push("/");
  };

  const getStartedHref = user ? "/dashboard" : "/register";
  const signInHref = user ? "#" : "/login";

  return (
    <motion.header
      className="fixed top-6 -translate-x-1/2 z-50"
      initial={{ left: "50%" }}
      animate={{ left: shouldCollapse ? "25%" : "50%" }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <motion.div
        className={`bg-white/80 backdrop-blur-md border border-gray-200 rounded-full shadow-lg flex items-center overflow-hidden ${shouldCollapse ? "px-2 py-2" : "px-6 py-3"
          }`}
        style={{ borderRadius: 9999 }}
        animate={{
          width: shouldCollapse ? "auto" : "auto"
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Logo Section */}
        <motion.div
          className="flex items-center gap-3 cursor-pointer"
          onClick={handleLogoClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="relative flex items-center justify-center w-10 h-10 bg-white rounded-full border border-gray-200">
            <Code className="w-5 h-5 text-blue-600" />
            {shouldCollapse && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute -right-0.5 -top-0.5 w-2.5 h-2.5 bg-blue-600 rounded-full border-2 border-white"
              />
            )}
          </div>

          <AnimatePresence mode="wait">
            {!shouldCollapse && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="text-lg font-bold text-gray-900 whitespace-nowrap overflow-hidden pr-2"
              >
                No-Code Node
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Navigation & Actions Wrapper */}
        <AnimatePresence>
          {!shouldCollapse && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex items-center overflow-hidden"
            >
              <div className="flex items-center gap-8 ml-4 mr-2">
                {/* Navigation Links */}
                <nav className="hidden md:flex items-center gap-8">
                  <NavLink href="#features">Features</NavLink>
                  <NavLink href="#pricing">Pricing</NavLink>
                  <NavLink href="/docs">Docs</NavLink>
                </nav>

                {/* Action Buttons */}
                <div className="flex items-center gap-4">
                  {user ? (
                    <button
                      onClick={handleLogout}
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
                    >
                      Logout
                    </button>
                  ) : (
                    <a
                      href="/login"
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
                    >
                      Sign In
                    </a>
                  )}
                  <a
                    href={getStartedHref}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap"
                  >
                    {user ? "Go to Dashboard" : "Get Started"}
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.header>
  );
};

export default Header;
